import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  TrendingUp,
  Users,
  FileCheck,
  ArrowRight,
  CheckCircle,
  Rocket,
  PiggyBank,
  Calendar,
} from "lucide-react";

const services = [
  {
    icon: Rocket,
    title: "Le développement",
    description: "Lancement et développement de votre activité freelance, de A à Z.",
    features: [
      "Négociation employeur (rupture conventionnelle,...)",
      "Dossier France Travail (reconversion, ARE)",
      "Création d'entreprise (admin, juridique)",
      "Passage de micro à société",
      "Ingénierie financière",
      "Accompagnement recherche de missions",
      "Conseil business",
    ],
    highlight: false,
  },
  {
    icon: FileCheck,
    title: "La gestion",
    description: "Gestion opérationnelle complète de votre activité une fois lancée.",
    features: [
      "Gestion administrative (courriers, relations admin)",
      "Gestion financière (comptabilité, déclarations)",
      "Gestion juridique (contrats, obligations)",
      "Support business (négociation TJM, relations intermédiaires)",
    ],
    highlight: false,
  },
  {
    icon: TrendingUp,
    title: "L'optimisation",
    description: "Optimisation de votre business et de vos finances, avant et pendant votre activité.",
    features: [
      "Optimisation de la rémunération (réduction charges sociales)",
      "Optimisations fiscales (réduction IS et IR)",
      "Optimisations financières (investissements, utilité cash)",
    ],
    highlight: true,
  },
  {
    icon: PiggyBank,
    title: "La diversification",
    description: "Pour les business établis : dérisquez et diversifiez vos revenus.",
    features: [
      "Stratégie d'investissement (immobilier, financier)",
      "Création & optimisation de holding",
      "Structuration de side-business (ESN, resto,...)",
      "Accès à notre Club Deal exclusif",
    ],
    highlight: false,
  },
];

export default function ServicesPage() {
  const revealRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1 }
    );

    revealRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const addToRefs = (el) => {
    if (el && !revealRefs.current.includes(el)) {
      revealRefs.current.push(el);
    }
  };

  return (
    <div className="overflow-hidden" data-testid="services-page">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-[#52fbb0]/10 text-slate-700 px-4 py-2 rounded-full text-sm font-medium mb-6 animate-fade-in">
              <Users className="h-4 w-4" />
              Nos services
            </div>
            <h1 className="font-manrope font-bold text-4xl md:text-5xl lg:text-6xl text-slate-900 mb-6 animate-fade-in-up">
              Des solutions adaptées à{" "}
              <span className="gradient-text">chaque freelance</span>
            </h1>
            <p className="text-slate-600 text-lg md:text-xl leading-relaxed animate-fade-in-up delay-200">
              Du choix du statut à l'optimisation fiscale, nous vous accompagnons à chaque étape 
              de votre parcours freelance avec expertise et bienveillance.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 md:py-32 bg-slate-50" data-testid="services-grid">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8" ref={addToRefs}>
            {services.map((service, index) => (
              <Card
                key={service.title}
                className={`service-card border-0 animate-fade-in ${
                  service.highlight ? "ring-2 ring-[#52fbb0]" : ""
                }`}
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`service-card-${index}`}
              >
                <CardContent className="p-8">
                  {service.highlight && (
                    <div className="inline-block bg-[#52fbb0] text-slate-900 text-xs font-semibold px-3 py-1 rounded-full mb-4">
                      Populaire
                    </div>
                  )}
                  <div className="w-16 h-16 bg-[#52fbb0]/10 rounded-2xl flex items-center justify-center mb-6">
                    <service.icon className="h-9 w-9 text-[#52fbb0]" />
                  </div>
                  <h3 className="font-manrope font-semibold text-xl text-slate-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 mb-6">{service.description}</p>
                  <ul className="space-y-3">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                        <CheckCircle className="h-4 w-4 text-[#52fbb0]" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-slate-900" data-testid="services-cta">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl mx-auto text-center" ref={addToRefs}>
            <h2 className="font-manrope font-bold text-3xl md:text-4xl lg:text-5xl text-white mb-6">
              Besoin d'un accompagnement personnalisé ?
            </h2>
            <p className="text-slate-300 text-lg md:text-xl mb-10">
              Notre équipe est disponible pour analyser votre situation et vous proposer 
              la solution la plus adaptée à vos objectifs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/rendez-vous"
                data-testid="services-rdv-cta"
              >
                <Button className="bg-[#52fbb0] text-slate-900 hover:bg-[#34d399] font-bold rounded-full px-10 py-6 text-lg">
                  <Calendar className="mr-2 h-5 w-5" />
                  Prendre rendez-vous
                </Button>
              </Link>
              <Link to="/contact" data-testid="services-contact-cta">
                <Button variant="outline" className="border-2 border-white/20 text-white hover:bg-white/10 font-semibold rounded-full px-10 py-6 text-lg">
                  Nous contacter
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
